"use client";
import React, { useState, useEffect, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import { Link } from "react-scroll";
import { RxCross1 } from "react-icons/rx";
import { IoMdMenu } from "react-icons/io";

/* ─────────────────────────────────────────
   NAV LINKS CONFIG
───────────────────────────────────────── */
const NAV_LINKS = [
  { to: "home", label: "Home", num: "01" },
  { to: "about", label: "About", num: "02" },
  { to: "education", label: "Education", num: "03" },
  { to: "experience", label: "Experience", num: "04" },
  { to: "projects", label: "Projects", num: "05" },
  { to: "contact", label: "Contact", num: "06" },
];

/* ─────────────────────────────────────────
   DESKTOP NAV LINK
───────────────────────────────────────── */
function NavLink({ to, label, num, onSetActive }) {
  const [active, setActive] = useState(false);

  const handleSetActive = () => {
    setActive(true);
    onSetActive?.(to);
  };
  const handleSetInactive = () => setActive(false);

  return (
    <Link
      to={to}
      spy
      smooth
      duration={600}
      offset={-80}
      onSetActive={handleSetActive}
      onSetInactive={handleSetInactive}
      className="relative group flex items-center gap-1.5 cursor-pointer py-1"
    >
      {/* Number */}
      <span
        className="text-[9px] tracking-[0.15em] text-white/20 group-hover:text-[#00f0ff]/50
          transition-colors duration-300"
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        {num}
      </span>

      {/* Label */}
      <span
        className={`text-sm tracking-[0.08em] uppercase font-medium transition-colors duration-300
          ${active ? "text-[#00f0ff]" : "text-white/50 group-hover:text-white/85"}`}
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        {label}
      </span>

      {/* Active underline */}
      <motion.span
        className="absolute bottom-0 left-0 h-px w-full"
        style={{ background: "linear-gradient(90deg, #0047ff, #00f0ff)" }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: active ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Hover underline (only when not active) */}
      {!active && (
        <span
          className="absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 group-hover:scale-x-100
            transition-transform duration-300"
          style={{ background: "rgba(255,255,255,0.12)" }}
        />
      )}
    </Link>
  );
}

/* ─────────────────────────────────────────
   MOBILE MENU LINK
───────────────────────────────────────── */
const mobileItemVariant = {
  hidden: { x: -30, opacity: 0 },
  show: (i) => ({
    x: 0,
    opacity: 1,
    transition: { duration: 0.45, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
  exit: { x: -20, opacity: 0, transition: { duration: 0.25 } },
};

function MobileNavLink({ to, label, num, index, onClose }) {
  return (
    <motion.div
      custom={index}
      variants={mobileItemVariant}
      initial="hidden"
      animate="show"
      exit="exit"
    >
      <Link
        to={to}
        spy
        smooth
        duration={600}
        offset={-80}
        onClick={onClose}
        activeClass="is-active"
        className="group flex items-center gap-4 py-4 border-b border-white/[0.05]
          cursor-pointer hover:border-[#00f0ff]/20 transition-colors duration-300"
      >
        <span
          className="text-[10px] text-white/20 group-hover:text-[#00f0ff]/40 transition-colors duration-300 w-5"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          {num}
        </span>
        <span
          className="text-2xl font-black text-white/70 group-hover:text-white transition-colors duration-300 tracking-tight"
          style={{ fontFamily: "'Syne', sans-serif" }}
        >
          {label}
        </span>
        <motion.span className="ml-auto text-[#00f0ff]/0 group-hover:text-[#00f0ff]/60 transition-colors duration-300 text-base">
          →
        </motion.span>
      </Link>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   MAIN NAVBAR
───────────────────────────────────────── */
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const { scrollYProgress } = useScroll();

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setScrollProgress(latest);
    setScrolled(latest > 0.01);
  });

  /* Lock body scroll when mobile menu open */
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      {/* ═══════════════════════════════════
          DESKTOP NAV
      ═══════════════════════════════════ */}
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 hidden lg:block"
      >
        {/* Backdrop blur layer */}
        <div
          className="absolute inset-0 transition-all duration-500"
          style={{
            background: scrolled ? "rgba(5,5,11,0.85)" : "rgba(5,5,11,0)",
            backdropFilter: scrolled ? "blur(20px)" : "blur(0px)",
            borderBottom: scrolled
              ? "1px solid rgba(255,255,255,0.04)"
              : "1px solid transparent",
          }}
        />

        {/* Scroll progress beam */}
        <motion.div
          className="absolute bottom-0 left-0 h-px origin-left"
          style={{
            scaleX: scrollProgress,
            background: "linear-gradient(90deg, #0047ff, #00f0ff)",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          {/* Logo */}
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
            <Link
              to="home"
              spy
              smooth
              duration={600}
              className="cursor-pointer"
            >
              <span
                className="text-xl font-black tracking-tight"
                style={{
                  fontFamily: "'Syne', sans-serif",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.5) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                &lt;Bokor
                <span
                  style={{
                    background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  /&gt;
                </span>
              </span>
            </Link>
          </motion.div>

          {/* Nav links */}
          <div className="flex items-center gap-7">
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} {...link} />
            ))}
          </div>

          {/* Resume CTA */}
          <motion.a
            href="/abubokor-full-stack-developer.pdf"
            download
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative group inline-flex items-center gap-2 px-5 py-2.5 rounded-full
              text-[#05050b] text-xs font-semibold tracking-[0.18em] uppercase overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #00f0ff, #0047ff)",
              boxShadow: "0 0 20px rgba(0,240,255,0.18)",
              fontFamily: "'DM Mono', monospace",
            }}
          >
            <span className="relative z-10">Resume</span>
            <span className="relative z-10 text-sm">↓</span>
            {/* Shimmer */}
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.25), transparent 60%)",
              }}
            />
          </motion.a>
        </div>
      </motion.nav>

      {/* ═══════════════════════════════════
          MOBILE NAV BAR
      ═══════════════════════════════════ */}
      <motion.div
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 lg:hidden"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 transition-all duration-400"
          style={{
            background: scrolled ? "rgba(5,5,11,0.9)" : "rgba(5,5,11,0)",
            backdropFilter: scrolled ? "blur(20px)" : "none",
            borderBottom: scrolled
              ? "1px solid rgba(255,255,255,0.04)"
              : "1px solid transparent",
          }}
        />

        {/* Progress */}
        <motion.div
          className="absolute bottom-0 left-0 h-px origin-left"
          style={{
            scaleX: scrollProgress,
            background: "linear-gradient(90deg, #0047ff, #00f0ff)",
          }}
        />

        <div className="relative z-10 flex items-center justify-between px-5 py-4">
          {/* Logo */}
          <Link to="home" spy smooth duration={600} className="cursor-pointer">
            <span
              className="text-lg font-black"
              style={{
                fontFamily: "'Syne', sans-serif",
                background:
                  "linear-gradient(135deg, #ffffff, rgba(255,255,255,0.5))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              &lt;Bokor
              <span
                style={{
                  background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                /&gt;
              </span>
            </span>
          </Link>

          {/* Hamburger */}
          <motion.button
            onClick={() => setIsOpen(true)}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            className="w-10 h-10 rounded-xl flex items-center justify-center
              border border-white/[0.08] bg-white/[0.03]
              hover:border-[#00f0ff]/30 hover:bg-[#00f0ff]/[0.06]
              transition-colors duration-300"
          >
            <IoMdMenu className="w-5 h-5 text-white/70" />
          </motion.button>
        </div>
      </motion.div>

      {/* ═══════════════════════════════════
          MOBILE DRAWER MENU
      ═══════════════════════════════════ */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop overlay */}
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            />

            {/* Drawer panel */}
            <motion.div
              key="drawer"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 left-0 bottom-0 z-[70] w-[min(85vw,360px)] flex flex-col overflow-hidden"
              style={{
                background: "#07070f",
                borderRight: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              {/* Drawer top glow */}
              <div
                className="absolute top-0 left-0 right-0 h-px pointer-events-none"
                style={{
                  background:
                    "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
                }}
              />

              {/* Orb accent */}
              <div
                className="absolute top-0 left-0 w-64 h-64 pointer-events-none opacity-20"
                style={{
                  background:
                    "radial-gradient(circle, rgba(0,71,255,0.4) 0%, transparent 70%)",
                  filter: "blur(60px)",
                }}
              />

              {/* Header */}
              <div className="relative z-10 flex items-center justify-between px-7 pt-7 pb-6">
                <Link
                  to="home"
                  spy
                  smooth
                  duration={600}
                  onClick={() => setIsOpen(false)}
                  className="cursor-pointer"
                >
                  <span
                    className="text-lg font-black"
                    style={{
                      fontFamily: "'Syne', sans-serif",
                      background:
                        "linear-gradient(135deg, #ffffff, rgba(255,255,255,0.5))",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    &lt;Bokor
                    <span
                      style={{
                        background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      /&gt;
                    </span>
                  </span>
                </Link>

                <motion.button
                  onClick={() => setIsOpen(false)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.25 }}
                  className="w-9 h-9 rounded-full flex items-center justify-center
                    border border-white/[0.08] bg-white/[0.03]
                    hover:border-red-400/30 hover:bg-red-400/[0.06]
                    transition-colors duration-300"
                >
                  <RxCross1 className="w-4 h-4 text-white/50" />
                </motion.button>
              </div>

              {/* Divider */}
              <div
                className="relative z-10 mx-7 h-px"
                style={{
                  background: "linear-gradient(90deg, #0047ff40, transparent)",
                }}
              />

              {/* Links */}
              <nav className="relative z-10 flex-1 px-7 pt-4 overflow-y-auto">
                {NAV_LINKS.map((link, i) => (
                  <MobileNavLink
                    key={link.to}
                    {...link}
                    index={i}
                    onClose={() => setIsOpen(false)}
                  />
                ))}
              </nav>

              {/* Drawer footer */}
              <div className="relative z-10 px-7 py-7">
                <div
                  className="h-px w-full mb-6"
                  style={{
                    background:
                      "linear-gradient(90deg, #0047ff30, transparent)",
                  }}
                />
                <motion.a
                  href="/abubokor-full-stack-developer.pdf"
                  download
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="relative group w-full flex items-center justify-center gap-3
                    py-3.5 rounded-xl font-semibold text-sm tracking-[0.18em] uppercase
                    text-[#05050b] overflow-hidden"
                  style={{
                    background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                    fontFamily: "'DM Mono', monospace",
                    boxShadow: "0 0 24px rgba(0,240,255,0.2)",
                  }}
                >
                  <span className="relative z-10">Download Resume</span>
                  <span className="relative z-10">↓</span>
                  <span
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.2), transparent)",
                    }}
                  />
                </motion.a>

                <p
                  className="mt-5 text-center text-[9px] tracking-[0.22em] uppercase text-white/18"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  Abu Bokor — Full Stack Engineer
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Mono:ital,wght@0,300;0,400;0,500&display=swap');
      `}</style>
    </>
  );
};

export default Navbar;
